/**
 * StatsSummary - Summary statistics cards for fitness analytics
 */

import { motion } from 'framer-motion';
import {
  Calendar,
  Dumbbell,
  TrendingUp,
  Flame,
  Target,
  Repeat,
  Award,
  BarChart3
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subValue, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors"
  >
    <div className="flex items-start justify-between">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      {subValue && (
        <span className="text-xs text-gray-500">{subValue}</span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
    </div>
  </motion.div>
);

const StatsSummary = ({ stats, dateRange }) => {
  if (!stats) return null;

  const formatVolume = (vol) => {
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(1)}K`;
    return vol.toFixed(0);
  };

  // Show actual data span, not selected range
  const getDateRangeLabel = () => {
    if (stats.actualDaysSpan) {
      const days = stats.actualDaysSpan;
      if (days >= 365) {
        const years = (days / 365).toFixed(1);
        return `${years} yıllık veri`;
      } else if (days >= 30) {
        const months = Math.round(days / 30);
        return `${months} aylık veri`;
      }
      return `${days} günlük veri`;
    }
    return dateRange >= 9999 ? 'Tüm zamanlar' : `Son ${dateRange} gün`;
  };

  const cards = [
    {
      icon: Calendar,
      label: 'Antrenman Günü',
      value: stats.totalDays,
      subValue: getDateRangeLabel(),
      color: 'bg-blue-500/20 text-blue-400',
    },
    {
      icon: Flame,
      label: 'Güncel Seri',
      value: `${stats.streak} gün`,
      subValue: stats.streak > 0 ? 'Devam et!' : '',
      color: 'bg-orange-500/20 text-orange-400',
    },
    {
      icon: BarChart3,
      label: 'Haftalık Ortalama',
      value: `${stats.avgPerWeek}`,
      subValue: 'gün/hafta',
      color: 'bg-green-500/20 text-green-400',
    },
    {
      icon: Dumbbell,
      label: 'Toplam Set',
      value: stats.totalSets.toLocaleString(),
      subValue: `${stats.totalReps.toLocaleString()} tekrar`,
      color: 'bg-purple-500/20 text-purple-400',
    },
    {
      icon: TrendingUp,
      label: 'Toplam Hacim',
      value: `${formatVolume(stats.totalVolume)} kg`,
      subValue: 'kaldırıldı',
      color: 'bg-red-500/20 text-red-400',
    },
    {
      icon: Target,
      label: 'Farklı Egzersiz',
      value: stats.uniqueExercises,
      subValue: 'çeşit',
      color: 'bg-cyan-500/20 text-cyan-400',
    },
    {
      icon: Award,
      label: 'En Çok Yapılan',
      value: stats.mostFrequent?.name || '-',
      subValue: stats.mostFrequent ? `${stats.mostFrequent.count} set` : '',
      color: 'bg-yellow-500/20 text-yellow-400',
    },
    {
      icon: Repeat,
      label: 'Set/Gün Ortalaması',
      value: (stats.totalSets / stats.totalDays).toFixed(1),
      subValue: 'set/antrenman',
      color: 'bg-pink-500/20 text-pink-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card, idx) => (
        <StatCard key={card.label} {...card} delay={idx * 0.05} />
      ))}
    </div>
  );
};

export default StatsSummary;
